import { z } from 'zod'
import {
  transformFiltersForBackend,
  type QueryFilterGroup,
  type QueryFilterNode,
} from '@/services/api/scanner-types'
import { HistoryTagFilterSchema } from '@/features/ticker-stats/schemas/stats-types'
import { storedFiltersToQuery } from '@/features/sliced-form/mapping/profile-to-query'
import type { ColumnCatalogDef } from '@/types/column-catalog'

// ─────────────────────────────────────────────────────────────────────────────
// Panel config (editable). A "stage" is one ordered categorical split of the
// population. Three modes, all of which serialize to a backend stage payload
// (either a QueryColumn dict or a QueryCaseList dict).
// ─────────────────────────────────────────────────────────────────────────────

export type SankeyStageMode = 'condition' | 'threshold' | 'category'

/** A single labelled bucket in condition mode — a name + a FilterForm group. */
export interface SankeyConditionBucket {
  id: string
  label: string
  /** Frontend QueryFilterGroup shape (FilterForm-editable); transformed on serialize. */
  filter: QueryFilterGroup
  /**
   * The bucket's rows in the form's shape, kept only while one is hidden with the eye: `filter`
   * has no place for it, and the sheet reopens on these. Written with `filter`, never alone.
   */
  form?: Array<Record<string, unknown>>
}

export interface SankeyStage {
  id: string
  /** Display name for the stage (UI only; response keys are positional stageN). */
  label: string
  mode: SankeyStageMode
  // condition mode
  buckets?: SankeyConditionBucket[]
  elseLabel?: string
  // threshold + category modes
  column?: string
  // threshold mode: ascending numeric edges → ranges (<e0, e0–e1, …, eLast+)
  edges?: number[]
  // category mode: fold all but the top-N distinct values into "Other"
  topN?: number
}

/**
 * A built-in named set of stages, the preset shape the Stages settings sheet
 * loads from `SANKEY_PRESETS` (spec 2026-09-18-history-presets-gallery-
 * design: nothing is saved any more). The index signature is kept so the
 * shape stays compatible with the generic profile-body constraint used by the
 * surfaces that still persist profiles.
 */
export interface SankeyStageProfile {
  id: string
  name: string
  stages: SankeyStage[]
  createdAt?: string
  updatedAt?: string
  [key: string]: unknown
}

// ─────────────────────────────────────────────────────────────────────────────
// Serialization: SankeyStage → backend stage payload
// ─────────────────────────────────────────────────────────────────────────────

/** A backend `when` clause built from a single column-vs-value/column comparison. */
function whenNode(
  column: string,
  operator: string,
  right: { value: unknown } | { column: string },
): QueryFilterGroup {
  return transformFiltersForBackend({
    AND: [{ left: { column }, operator, right }],
  })
}

/** Threshold labels: <e0, e0–e1, …, and the else label eLast+. */
export function thresholdLabels(edges: number[]): {
  caseLabels: string[]
  elseLabel: string
} {
  const caseLabels = edges.map((edge, i) => (i === 0 ? `<${edges[0]}` : `${edges[i - 1]}–${edge}`))
  const elseLabel = edges.length ? `${edges[edges.length - 1]}+` : 'Other'
  return { caseLabels, elseLabel }
}

/** Serialize one stage to the dict the /history/sankey endpoint accepts. */
export function serializeSankeyStage(stage: SankeyStage): Record<string, unknown> {
  if (stage.mode === 'category') {
    return { column: stage.column }
  }

  if (stage.mode === 'threshold') {
    const edges = stage.edges ?? []
    const col = stage.column ?? ''
    const { caseLabels, elseLabel } = thresholdLabels(edges)
    // Ordered CASE: first match wins, so chained `< edge` yields disjoint ranges.
    const cases = edges.map((edge, i) => [
      whenNode(col, 'lt', { value: edge }),
      { value: caseLabels[i] },
    ])
    return { cases, else: { value: elseLabel } }
  }

  // condition mode
  const cases = (stage.buckets ?? []).map((b) => [
    transformFiltersForBackend(b.filter),
    { value: b.label },
  ])
  return { cases, else: { value: stage.elseLabel ?? 'Other' } }
}

export function serializeSankeyStages(stages: SankeyStage[]): Record<string, unknown>[] {
  return stages.map(serializeSankeyStage)
}

/** Positional response keys the backend emits (stage0..stageN). */
export function sankeyStageKeys(count: number): string[] {
  return Array.from({ length: count }, (_, i) => `stage${i}`)
}

export const SANKEY_MAX_STAGES = 4
export const SANKEY_MAX_BUCKETS = 6

function genId(prefix: string): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)
  return `${prefix}-${rand}`
}

/** A fresh empty condition bucket (label + empty filter). */
export function createConditionBucket(): SankeyConditionBucket {
  return { id: genId('bucket'), label: 'New Bucket', filter: { AND: [] } }
}

function hasHiddenRow(node: unknown): boolean {
  if (Array.isArray(node)) return node.some(hasHiddenRow)
  if (!node || typeof node !== 'object') return false
  const n = node as Record<string, unknown>
  return n.enabled === false || hasHiddenRow(n.AND) || hasHiddenRow(n.OR)
}

/**
 * A bucket's condition from its form rows. The rows are kept only while one is hidden with the
 * eye (the condition has no place for it), so a bucket without hidden rows stays exactly as the
 * presets write it and opening the sheet never reads as an edit.
 */
export function withConditionRows(
  bucket: SankeyConditionBucket,
  rows: Array<Record<string, unknown>>,
  columnMap: Record<string, ColumnCatalogDef>,
): SankeyConditionBucket {
  const { form: _previous, ...rest } = bucket
  void _previous
  const filter = storedFiltersToQuery(rows, columnMap)
  return hasHiddenRow(rows) ? { ...rest, filter, form: rows } : { ...rest, filter }
}

/** A fresh stage in the given mode with sensible defaults. */
export function createSankeyStage(mode: SankeyStageMode): SankeyStage {
  const base = { id: genId('stage'), label: 'New Stage', mode }
  if (mode === 'condition') {
    return { ...base, buckets: [createConditionBucket()], elseLabel: 'Other' }
  }
  if (mode === 'threshold') {
    return { ...base, column: '', edges: [10, 20, 30] }
  }
  return { ...base, column: 'sector', topN: 8 }
}

/**
 * Default breakdown: broke-PM-high → closed-above/below-open → next-day
 * higher/lower. The universe (e.g. gap ≥ 30%) comes from the page's global
 * history filters, not a stage. Three stages by default so the flow reads as a
 * multi-tier story (and exercises label placement at realistic density).
 */
export const DEFAULT_SANKEY_STAGES: SankeyStage[] = [
  {
    id: 'stage-pm-high',
    label: 'PM High',
    mode: 'condition',
    elseLabel: 'Did Not Break',
    buckets: [
      {
        id: 'bucket-broke-pm-high',
        label: 'Broke PM High',
        filter: {
          AND: [
            {
              left: { column: 'day0_high' },
              operator: 'gt',
              right: { column: 'day0_pm_high' },
            },
          ],
        },
      },
    ],
  },
  {
    id: 'stage-close-vs-open',
    label: 'Close vs Open',
    mode: 'condition',
    elseLabel: 'Closed Below Open',
    buckets: [
      {
        id: 'bucket-closed-above-open',
        label: 'Closed Above Open',
        filter: {
          AND: [
            {
              left: { column: 'day0_close' },
              operator: 'gt',
              right: { column: 'day0_open' },
            },
          ],
        },
      },
    ],
  },
  {
    id: 'stage-next-day',
    label: 'Next Day',
    mode: 'condition',
    // Missing next-day data (no day1 bar) falls into "Next Day Lower".
    elseLabel: 'Next Day Lower',
    buckets: [
      {
        id: 'bucket-next-day-higher',
        label: 'Next Day Higher',
        filter: {
          AND: [
            {
              left: { column: 'day1_close' },
              operator: 'gt',
              right: { column: 'day0_close' },
            },
          ],
        },
      },
    ],
  },
]

// ─────────────────────────────────────────────────────────────────────────────
// Request / response schemas
// ─────────────────────────────────────────────────────────────────────────────

export const SankeyBreakdownRequestSchema = z.object({
  stages: z.array(z.record(z.unknown())),
  filters: z.record(z.unknown()).default({}),
  adjusted: z.boolean().default(false),
  // Tag memberships on the universe filter, resolved by the backend.
  tag_filter: HistoryTagFilterSchema.optional(),
})
export type SankeyBreakdownRequest = z.infer<typeof SankeyBreakdownRequestSchema>

export const SankeyBreakdownResponseSchema = z.object({
  rows: z.array(z.record(z.unknown())),
})
export type SankeyBreakdownResponse = z.infer<typeof SankeyBreakdownResponseSchema>

// ─────────────────────────────────────────────────────────────────────────────
// Cross-tab → AG Charts sankey link transform
// ─────────────────────────────────────────────────────────────────────────────

export interface SankeyLink {
  from: string
  to: string
  size: number
}

export interface SankeyFoldNotice {
  stageIndex: number
  stageKey: string
  /** How many distinct values were folded into the "Other" node. */
  foldedCount: number
}

export interface SankeyTransformResult {
  links: SankeyLink[]
  foldedStages: SankeyFoldNotice[]
}

const NAMESPACE_SEP = '·'
const OTHER_LABEL = 'Other'

/**
 * Namespace a node by its stage index. AG Charts keys sankey nodes by name
 * string, so without this a bucket named "Yes" in two stages would merge into
 * one node. The stage-index prefix keeps node identity unique per stage.
 */
export function sankeyNodeId(stageIndex: number, value: string): string {
  return `${stageIndex}${NAMESPACE_SEP}${value}`
}

/** Strip the "K·" stage prefix for display (labels/tooltips). */
export function stripSankeyNodePrefix(id: string): string {
  const idx = id.indexOf(NAMESPACE_SEP)
  return idx === -1 ? id : id.slice(idx + 1)
}

/** Inverse of sankeyNodeId — parse "K·label" back into stage index + label. */
export function parseSankeyNodeId(id: string): { stageIndex: number; label: string } | null {
  const sep = id.indexOf(NAMESPACE_SEP)
  if (sep < 0) return null
  const stageIndex = Number(id.slice(0, sep))
  if (!Number.isInteger(stageIndex)) return null
  return { stageIndex, label: id.slice(sep + 1) }
}

function coerceValue(v: unknown): string {
  return v == null ? 'Unknown' : String(v)
}

function coerceCount(v: unknown): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/**
 * Turn a cross-tab (rows of {stage0, stage1, …, n}) into AG Charts sankey
 * links. Links are emitted only between adjacent stages (alluvial), summed by
 * bucket pair. Per stage, all but the top-N values (by total count) fold into
 * an "Other" node, reported via `foldedStages` so callers never silently
 * truncate. `topNByStage` sets a stage's own N (a Groups stage's "Top N");
 * a stage without one uses `topN` (default 8).
 */
export function crossTabToSankeyLinks(
  rows: Array<Record<string, unknown>>,
  stageKeys: string[],
  opts: { topN?: number; topNByStage?: Array<number | undefined> } = {},
): SankeyTransformResult {
  const sharedTopN = opts.topN ?? 8
  const foldedStages: SankeyFoldNotice[] = []

  // Per stage: map every raw value → its display value (itself, or "Other").
  const displayMaps: Array<Map<string, string>> = stageKeys.map((key, si) => {
    const topN = opts.topNByStage?.[si] ?? sharedTopN
    const totals = new Map<string, number>()
    for (const row of rows) {
      const v = coerceValue(row[key])
      totals.set(v, (totals.get(v) ?? 0) + coerceCount(row.n))
    }
    const sorted = [...totals.entries()].sort((a, b) => b[1] - a[1])
    const keep = new Set(sorted.slice(0, topN).map(([v]) => v))
    const map = new Map<string, string>()
    for (const v of totals.keys()) map.set(v, keep.has(v) ? v : OTHER_LABEL)
    if (totals.size > topN) {
      foldedStages.push({
        stageIndex: si,
        stageKey: key,
        foldedCount: totals.size - topN,
      })
    }
    return map
  })

  // Keyed by JSON.stringify([from, to]) — node ids can contain any character
  // (bucket labels are arbitrary), so a delimiter-based key would be unsafe.
  const linkTotals = new Map<string, number>()
  for (const row of rows) {
    const n = coerceCount(row.n)
    if (n <= 0) continue
    for (let si = 0; si < stageKeys.length - 1; si++) {
      const fromV = displayMaps[si].get(coerceValue(row[stageKeys[si]])) ?? OTHER_LABEL
      const toV = displayMaps[si + 1].get(coerceValue(row[stageKeys[si + 1]])) ?? OTHER_LABEL
      const key = JSON.stringify([sankeyNodeId(si, fromV), sankeyNodeId(si + 1, toV)])
      linkTotals.set(key, (linkTotals.get(key) ?? 0) + n)
    }
  }

  const links: SankeyLink[] = [...linkTotals.entries()].map(([key, size]) => {
    const [from, to] = JSON.parse(key) as [string, string]
    return { from, to, size }
  })

  return { links, foldedStages }
}

// ─────────────────────────────────────────────────────────────────────────────
// Tooltip percentages
// ─────────────────────────────────────────────────────────────────────────────

export interface SankeyTotals {
  /** Total population = combined outflow of the first stage (alluvial conserves). */
  total: number
  /** Per source-node outflow — denominator for a link's conditional %. */
  outflow: Map<string, number>
  /** Per-node throughput (max of in/out), keyed by namespaced node id. */
  nodeTotal: Map<string, number>
  /**
   * Same throughput keyed by the *stripped* label — AG Charts hands the node
   * tooltip the display label (prefix already removed), so the namespaced
   * `nodeTotal` lookup misses; this is the fallback.
   */
  nodeTotalByLabel: Map<string, number>
}

const STAGE0_PREFIX = `0${NAMESPACE_SEP}`

/** Pre-aggregate the counts a tooltip needs (built once per link set). */
export function buildSankeyTotals(links: SankeyLink[]): SankeyTotals {
  const outflow = new Map<string, number>()
  const inflow = new Map<string, number>()
  let total = 0
  for (const l of links) {
    outflow.set(l.from, (outflow.get(l.from) ?? 0) + l.size)
    inflow.set(l.to, (inflow.get(l.to) ?? 0) + l.size)
    if (l.from.startsWith(STAGE0_PREFIX)) total += l.size
  }
  const nodeTotal = new Map<string, number>()
  const nodeTotalByLabel = new Map<string, number>()
  for (const id of new Set([...outflow.keys(), ...inflow.keys()])) {
    const v = Math.max(outflow.get(id) ?? 0, inflow.get(id) ?? 0)
    nodeTotal.set(id, v)
    nodeTotalByLabel.set(stripSankeyNodePrefix(id), v)
  }
  return { total, outflow, nodeTotal, nodeTotalByLabel }
}

function pct(n: number, denom: number): number {
  return denom > 0 ? Math.round((n / denom) * 100) : 0
}

export interface SankeyTooltipData {
  title: string
  count: number
  /** % of the whole population. */
  sharePct: number
  /** % of the source node's outflow (links only; null for node hovers). */
  condPct: number | null
  /** Source-node label (links only; null for node hovers). */
  sourceLabel: string | null
  /** Source-node total — the denominator behind "156 of 204" (links only). */
  sourceCount: number | null
}

/**
 * The numbers a sankey tooltip shows. A **link** hover carries the conditional
 * share of its source node ("76% of Did Not Break") plus the "N of M" fraction
 * and the share of the whole population; a **node** hover carries the node's
 * throughput + share.
 */
export function computeSankeyTooltip(
  params: { datum?: Record<string, unknown>; title?: unknown; size?: unknown },
  totals: SankeyTotals,
): SankeyTooltipData {
  const d = params.datum ?? {}
  const from = d.from
  const to = d.to
  const size = typeof params.size === 'number' ? params.size : Number(d.size)

  // Link hover — conditional flow from the previous step.
  if (from != null && to != null && Number.isFinite(size)) {
    const fromL = stripSankeyNodePrefix(String(from))
    const toL = stripSankeyNodePrefix(String(to))
    const sourceCount = totals.outflow.get(String(from)) ?? size
    return {
      title: `${fromL} → ${toL}`,
      count: size,
      sharePct: pct(size, totals.total),
      condPct: pct(size, sourceCount),
      sourceLabel: fromL,
      sourceCount,
    }
  }

  // Node hover — throughput + share of the population. AG hands us the stripped
  // display label, so fall back to the by-label map when the namespaced lookup
  // misses.
  const id = String(params.title ?? d.label ?? d.id ?? '')
  const label = stripSankeyNodePrefix(id)
  const nt = totals.nodeTotal.get(id) ?? totals.nodeTotalByLabel.get(label) ?? 0
  return {
    title: label,
    count: nt,
    sharePct: pct(nt, totals.total),
    condPct: null,
    sourceLabel: null,
    sourceCount: null,
  }
}

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c])
}

/**
 * AG Charts renders an object-returning tooltip renderer as a *structured*
 * tooltip (title + data rows) and ignores unknown keys. Returning a single
 * root element (raw HTML) renders it verbatim — so we own the whole layout with
 * inline styles (padding + hierarchy) rather than relying on AG's classes.
 *
 * Links lead with the conversion rate ("80% of Did Not Break") — the "what
 * happens next" insight — with the count + population share muted below. Nodes
 * lead with the count.
 */
export function sankeyTooltipHtml(
  params: { datum?: Record<string, unknown>; title?: unknown; size?: unknown },
  totals: SankeyTotals,
): string {
  const t = computeSankeyTooltip(params, totals)
  const count = t.count.toLocaleString()
  const small = 'font-weight:400;font-size:11px;opacity:0.75'
  const isLink = t.condPct != null && t.sourceLabel != null && t.sourceCount != null

  // Links: "156/204 | 76%" of the source, with the population share muted below.
  // Nodes: lead with the count.
  const headline = isLink
    ? `${count}/${(t.sourceCount as number).toLocaleString()} | ${t.condPct}%` +
      `<span style="${small}"> of ${escapeHtml(t.sourceLabel as string)}</span>`
    : `${count}<span style="${small}"> setups</span>`

  return (
    `<div style="padding:8px 11px;font-family:var(--font-sans);line-height:1.4;white-space:nowrap">` +
    `<div style="font-weight:600;font-size:12px;margin-bottom:5px">${escapeHtml(t.title)}</div>` +
    `<div style="font-weight:600;font-size:14px">${headline}</div>` +
    `<div style="font-size:11px;opacity:0.55;margin-top:3px">${t.sharePct}% of all records</div>` +
    `</div>`
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Click-to-drill: map a clicked node/link back to a filter predicate
// ─────────────────────────────────────────────────────────────────────────────

// Operator flips for negating an else bucket ("none of the buckets matched").
const NEGATE_OP: Record<string, string> = {
  gt: 'lte',
  gte: 'lt',
  lt: 'gte',
  lte: 'gt',
  eq: 'neq',
  neq: 'eq',
}

/**
 * The else bucket = "no bucket's condition matched" = AND of NOT(each bucket).
 * Handled for single-comparison buckets (the common case) by flipping each
 * operator; returns null when a bucket is multi-condition or uses a
 * non-invertible operator (between/in/…), so the drill degrades gracefully.
 */
function negateConditionBuckets(buckets: SankeyConditionBucket[]): QueryFilterGroup | null {
  const leaves: QueryFilterNode[] = []
  for (const b of buckets) {
    const and = b.filter.AND
    if (!and || and.length !== 1) return null
    const leaf = and[0] as QueryFilterNode
    if (!('operator' in leaf)) return null // nested group
    const neg = NEGATE_OP[leaf.operator]
    if (!neg) return null
    leaves.push({ ...leaf, operator: neg })
  }
  return leaves.length ? { AND: leaves } : null
}

/**
 * The frontend-shape filter that isolates the population of a single node
 * (one bucket in one stage). Returns null for un-drillable nodes — the folded
 * "Other" bucket, or an else bucket that can't be cleanly negated.
 */
export function bucketPredicateForNode(
  stages: SankeyStage[],
  nodeId: string,
): QueryFilterGroup | null {
  const parsed = parseSankeyNodeId(nodeId)
  if (!parsed) return null
  const stage = stages[parsed.stageIndex]
  if (!stage || parsed.label === OTHER_LABEL) return null
  const { label } = parsed

  if (stage.mode === 'category') {
    if (!stage.column) return null
    return { AND: [{ left: { column: stage.column }, operator: 'eq', right: { value: label } }] }
  }

  if (stage.mode === 'condition') {
    const bucket = stage.buckets?.find((b) => b.label === label)
    if (bucket) return bucket.filter
    // else bucket → negate every bucket's condition so the cascade is complete.
    if (label === (stage.elseLabel ?? 'Other')) {
      return negateConditionBuckets(stage.buckets ?? [])
    }
    return null
  }

  // threshold
  const col = stage.column
  const edges = stage.edges ?? []
  if (!col || edges.length === 0) return null
  const { caseLabels, elseLabel } = thresholdLabels(edges)
  if (label === elseLabel) {
    return {
      AND: [{ left: { column: col }, operator: 'gte', right: { value: edges[edges.length - 1] } }],
    }
  }
  const i = caseLabels.indexOf(label)
  if (i < 0) return null
  if (i === 0) {
    return { AND: [{ left: { column: col }, operator: 'lt', right: { value: edges[0] } }] }
  }
  return {
    AND: [
      { left: { column: col }, operator: 'gte', right: { value: edges[i - 1] } },
      { left: { column: col }, operator: 'lt', right: { value: edges[i] } },
    ],
  }
}

/** Combined predicate for a link (source bucket AND target bucket). */
export function linkDrillPredicate(
  stages: SankeyStage[],
  fromId: string,
  toId: string,
): QueryFilterGroup | null {
  const parts = [
    bucketPredicateForNode(stages, fromId),
    bucketPredicateForNode(stages, toId),
  ].filter((p): p is QueryFilterGroup => p != null)
  if (parts.length === 0) return null
  return { AND: parts.flatMap((p) => p.AND ?? []) }
}

/** AND a drill predicate into the existing page filters (both frontend-shape). */
export function mergeHistoryFilters(
  current: QueryFilterGroup | undefined,
  predicate: QueryFilterGroup,
): QueryFilterGroup {
  const currentAnd = current?.AND ?? (current ? [current] : [])
  return { AND: [...currentAnd, ...(predicate.AND ?? [])] }
}

// ─────────────────────────────────────────────────────────────────────────────
// Preset breakdowns
// ─────────────────────────────────────────────────────────────────────────────

export interface SankeyPreset {
  id: string
  name: string
  stages: SankeyStage[]
}

function conditionStage(
  id: string,
  label: string,
  bucketLabel: string,
  left: string,
  right: string,
  elseLabel: string,
): SankeyStage {
  return {
    id,
    label,
    mode: 'condition',
    elseLabel,
    buckets: [
      {
        id: `${id}-bucket`,
        label: bucketLabel,
        filter: { AND: [{ left: { column: left }, operator: 'gt', right: { column: right } }] },
      },
    ],
  }
}

/** One-click starting points shown in the stage builder. */
export const SANKEY_PRESETS: SankeyPreset[] = [
  // The default is now the 3-stage PM High → Close → Next Day; this preset keeps
  // the simpler 2-stage view available (the first two default stages).
  { id: 'pm-close', name: 'PM High → Close', stages: DEFAULT_SANKEY_STAGES.slice(0, 2) },
  {
    id: 'gap-close',
    name: 'Gap Tiers → Close',
    stages: [
      {
        id: 'preset-gap-tiers',
        label: 'Gap',
        mode: 'threshold',
        column: 'day0_gap_pct',
        edges: [10, 20, 30, 50],
      },
      conditionStage(
        'preset-gap-close',
        'Close vs Open',
        'Closed Above Open',
        'day0_close',
        'day0_open',
        'Closed Below Open',
      ),
    ],
  },
  {
    id: 'sector-close',
    name: 'Sector → Close',
    stages: [
      { id: 'preset-sector', label: 'Sector', mode: 'category', column: 'sector', topN: 8 },
      conditionStage(
        'preset-sector-close',
        'Close vs Open',
        'Closed Above Open',
        'day0_close',
        'day0_open',
        'Closed Below Open',
      ),
    ],
  },
  {
    id: 'pm-close-next',
    name: 'PM High → Close → Next Day',
    stages: [
      conditionStage(
        'preset-pm',
        'PM High',
        'Broke PM High',
        'day0_high',
        'day0_pm_high',
        'Did Not Break',
      ),
      conditionStage(
        'preset-close',
        'Close vs Open',
        'Closed Above Open',
        'day0_close',
        'day0_open',
        'Closed Below Open',
      ),
      conditionStage(
        'preset-next-day',
        'Next Day',
        'Next Day Higher',
        'day1_close',
        'day0_close',
        'Next Day Lower',
      ),
    ],
  },
]
